/**
 * BRAND CONFIGURATION — Single Source of Truth
 * ============================================
 *
 * Every display string, asset URL and brand identifier visible on the
 * public site, login screens and admin sidebar lives in this file.
 *
 * 📦 To rebrand this project for a new brand (e.g. Red Chief):
 *   1. Edit the values below.
 *   2. Edit colour values at the top of `frontend/src/index.css`
 *      (the `--blackberrys-*` CSS variables — keep the variable NAMES, just
 *      swap the hex values to match the new brand palette).
 *   3. Edit `<title>` + `<meta>` tags in `frontend/public/index.html`
 *      (these are static / build-time and cannot read this file).
 *   4. Edit `BRAND_NAME` + super-admin / brand-admin emails in
 *      `backend/.env`.
 *   5. Replace hero / boutique / editorial Unsplash image URLs below
 *      with brand-appropriate imagery (or upload your own via the
 *      Public Site CMS once logged in).
 *
 * 🔒 Do NOT rename CSS class names (`blackberrys-text-burgundy`, etc.) —
 *    those are stable selectors used across 50+ components. Only the
 *    underlying CSS variable VALUES need to change per brand.
 */

export const BRAND = {
  // -- Identity --------------------------------------------------------
  name: "BLACKBERRYS",                          // Short display name (uppercase logo)
  legalName: "BLACKBERRYS",                     // Used in © footer
  domain: "blackberrys.com",                    // Public-facing website
  shortDescriptor: "premium Indian menswear brand",

  // -- Platform identity (Fundle = the loyalty engine running this app)
  platform: "Fundle",
  poweredBy: "Powered by Fundle",
  aiAssistant: "Fundle Brain",
  // Real Fundle wordmark (white/light — shows on dark surfaces). Lives in /public.
  platformLogoUrl: "/fundle-logo.png",

  // -- Brand assets (real Blackberrys logo + campaign imagery, served from /public)
  logoUrl: "/blackberrys-logo.png",            // dark wordmark for light surfaces
  logoWhiteUrl: "/blackberrys-logo-white.png", // white wordmark for dark surfaces
  heroImageUrl: "/blackberrys-hero.jpg",
  editorialImageUrl: "/blackberrys-editorial.jpg",

  // -- Loyalty programme branding -------------------------------------
  loyaltyProgramName: "BLACKBERRYS Rewards",
  welcomePointsValue: 100,               // Bonus points credited on sign-up
  welcomeToast: "Welcome to BLACKBERRYS Rewards! 100 bonus points credited.",
  ctaJoinFree: "Join BLACKBERRYS Rewards Free",

  // -- Social --------------------------------------------------------
  social: {
    instagram: "https://instagram.com/blackberrys_brand",
    facebook: "https://facebook.com",
    youtube: "https://youtube.com",
  },

  // -- SEO / meta (also mirror in frontend/public/index.html) ---------
  meta: {
    title: "BLACKBERRYS Rewards — Powered by Fundle",
    description:
      "Join the official BLACKBERRYS loyalty programme. Earn points on every purchase, unlock exclusive tier privileges, birthday bonuses, and access VIP collections across India. Powered by Fundle.",
  },

  // -- Public home copy (CMS overrides take precedence at runtime) ----
  homeCopy: {
    heroEyebrow: "An Exclusive Programme · Powered by Fundle",
    heroSubtext:
      "The official BLACKBERRYS loyalty programme. Every purchase reveals new privileges — from welcome bonuses and birthday gifts to private VIP previews.",
    heroImageAlt: "Blackberrys premium menswear editorial",
    boutiqueImageAlt: "BLACKBERRYS boutique interior",
    editorialImageAlt: "Model wearing BLACKBERRYS collection",
  },

  // -- Footer copy ----------------------------------------------------
  footerTagline:
    "The official loyalty programme for BLACKBERRYS — where every purchase becomes a privilege. Tailored for the modern Indian gentleman.",

  // -- Login / portal copy --------------------------------------------
  loginCopy: {
    descriptor:
      "A unified suite for loyalty, CRM, analytics and campaigns — purpose-built for BLACKBERRYS.",
    imageAlt: "BLACKBERRYS",
  },

  // -- Brand colour palette ------------------------------------------
  // These hex values are injected as CSS variables at runtime
  // (see App.js → useEffect). Variable NAMES stay `--blackberrys-*` because
  // 50+ class names like `.blackberrys-text-burgundy` reference them — only
  // the VALUES change per brand.
  //
  // For Red Chief you might use:
  //   black: "#141414", cream: "#F8F4EE",
  //   burgundy: "#B91C1C", burgundyDeep: "#7F1212",
  //   champagne: "#1F2937", champagneLight: "#374151",
  colors: {
    black: "#141414",          // --blackberrys-black           (header / footer)
    cream: "#F6F3EC",          // --blackberrys-cream           (page background)
    burgundy: "#5A1A22",       // --blackberrys-burgundy        (primary brand accent)
    burgundyDeep: "#3E1017",   // --blackberrys-burgundy-deep   (hover state)
    champagne: "#B08D57",      // --blackberrys-champagne       (secondary accent / luxury hint)
    champagneLight: "#E6D6B8", // --blackberrys-champagne-light (light backgrounds)
  },
};

export default BRAND;
